import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import {
  Search,
  Download,
  ExternalLink,
  Box,
  AlertTriangle,
  Layers,
  ArrowRight,
  Loader2,
  RefreshCw,
  Info,
  Upload
} from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { backupStore } from "../../stores/backup.store";

import toast from "react-hot-toast";
import { Card } from '../design/Card';
import { Button } from '../design/Button';
import { Select } from '../design/Select';
import { Input } from '../design/Input';
import { Badge } from '../design/Badge';
import { cn } from '@/utils/cn';
import { apiUrl, getToken } from '../../utils/api';

interface Modpack {
  id: string;
  name: string;
  summary: string;
  downloads: number;
  iconUrl: string;
  slug: string;
  source: "modrinth" | "curseforge";
}

export const ModpacksTab = observer(() => {
  const [source, setSource] = useState<"modrinth" | "curseforge">("modrinth");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Modpack[]>([]);
  const [trending, setTrending] = useState<Modpack[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState<Modpack | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const [showVersionModal, setShowVersionModal] = useState(false);

  // Manual Upload State
  const [manualFile, setManualFile] = useState<File | null>(null);
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [manualRestart, setManualRestart] = useState(true);
  const [manualBackup, setManualBackup] = useState(true);

  // Install State
  const [installStatus, setInstallStatus] = useState<
    "idle" | "installing" | "complete" | "error"
  >("idle");
  const [installMessage, setInstallMessage] = useState("");
  // Upload-Fortschritt (Browser -> Server) in %, null = kein Upload aktiv.
  // Getrennt vom Install-Fortschritt (activeInstalls, serverseitig via Socket).
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const activeServer = serverStore.activeServer;
  const installState = activeServer
    ? serverStore.activeInstalls.get(activeServer.id)
    : null;

  const fetchTrending = async () => {
    setLoading(true);
    const trending = await serverStore.fetchTrendingModpacks(source);
    setTrending(trending);
    setLoading(false);
  };

  const searchModpacks = async () => {
    if (!query.trim()) {
      fetchTrending();
      setResults([]);
      return;
    }

    setLoading(true);
    setResults([]);
    const hits = await serverStore.searchModpacks(query, source);
    setResults(hits);
    setLoading(false);
  };

  useEffect(() => {
    if (!query) fetchTrending();
  }, [source]);

  const loadVersions = async (pack: Modpack) => {
    setSelectedPack(pack);
    setLoadingVersions(true);
    setVersions([]);
    
    // NOTE: pack.slug might not exist depending on where the pack object came from (e.g. fake placeholder)
    // but the store method handles it.
    const versions = await serverStore.fetchModpackVersions(pack.id, pack.slug, pack.source);
    setVersions(versions);
    setLoadingVersions(false);
  };

  // Linking Mode (for existing servers)
  const [linkingMode, setLinkingMode] = useState(false);

  const initiateInstall = async (version: any) => {
    const action = linkingMode ? "link" : "install";
    setInstallModal({ version, action });
  };

  // Function to actually call API
  const confirmInstall = async () => {
    if (!installModal || !activeServer) return;
    const { version, action } = installModal;

    // Helper to get file url if exists
    const file =
      version.files?.find((f: any) => f.primary) || version.files?.[0];

    // 1. Install
    setInstallStatus("installing");
    setInstallMessage(
      action === "link"
        ? "Verknüpfung wird gespeichert..."
        : createBackup
        ? "Backup wird erstellt..."
        : "Update gestartet..."
    );
    setInstallModal(null); // Close modal

    
      const body = {
        url: file?.url,
        restart: action === "install",
        modpackId: selectedPack?.id || activeServer?.installed_modpack?.id,
        modpackName:
          selectedPack?.name || activeServer?.installed_modpack?.name,
        versionName: version.version_number || version.name,
        versionId: version.id,
        source: selectedPack?.source || activeServer?.installed_modpack?.source,
        javaPath: selectedJava, // Pass selected Java path
        createBackup: createBackup, // Pass backup choice
        gameVersions: version.game_versions,
        loaders: version.loaders,
      };

      const res = await serverStore.installModpack(activeServer.id, action, body);

      if (res.success) {
        setInstallStatus("complete");
        toast.success(
          action === "link"
            ? "Modpack erfolgreich verknüpft!"
            : "Installation gestartet!"
        );

        // Clear update banner and refresh server data after short delay
        setUpdateAvailable(null);
        setTimeout(() => {
          serverStore.fetchData(); // Refetch all server data to update installed_modpack metadata
        }, 2000);

        if (action === "link") {
            // Ideally we shouldn't reload, but just refresh data. 
            // The original code reloaded, let's stick to fetchData which updates the UI via MobX.
        }
      } else {
        setInstallStatus("error");
        setInstallMessage(res.message || "Fehler.");
        toast.error(res.message || "Fehler beim Starten der Installation.");
      }
  };

  // Check for updates for installed pack
  const [updateAvailable, setUpdateAvailable] = useState<any | null>(null);

  // Java Versions
  const [javaVersions, setJavaVersions] = useState<string[]>([]);
  const [selectedJava, setSelectedJava] = useState<string>("");
  const [installModal, setInstallModal] = useState<{
    version: any;
    action: "install" | "link";
  } | null>(null);
  const [createBackup, setCreateBackup] = useState(true); // Default to true for safety

  useEffect(() => {
    if (activeServer?.installed_modpack) {
      checkUpdate();
    }
  }, [activeServer?.installed_modpack]);

  useEffect(() => {
    // Fetch Java Versions
    // This fetching is still direct because there's no cleaner place in ServerStore yet for general System info.
    // Ideally should be systemStore.fetchJavaVersions().
    // Leaving purely as API call for now is acceptable or move to ServerStore helper.
    // Let's replace it with a direct call but without apiFetch helper if I removed the import?
    // No, I need apiFetch for this one line or move it.
    // I will use serverStore.fetchJavaVersions if I add it.
    // Just adding fetchJavaVersions to serverStore for consistency.
    
    serverStore.fetchJavaVersions().then(versions => {
        setJavaVersions(versions);
        if (versions.length > 0) setSelectedJava(versions[0]);
    });
  }, []);

  // Modpack Details for Linked Pack
  const [linkedDetails, setLinkedDetails] = useState<Modpack | null>(null);

  const checkUpdate = async () => {
    const current = activeServer?.installed_modpack;
    if (!current) return;

    // Fetch versions
    const versions = await serverStore.fetchModpackVersions(current.id, current.id, current.source as any); // Slug might be missing in installed info, fallback to ID usage logic in store (though store expects slug for modrinth)
    // NOTE: installed_modpack DOES NOT have slug currently.
    // If it's modrinth, store.fetchModpackVersions will use slug endpoint.
    // If slug is missing (passed id as slug), modrinth API might fail if it strictly expects slug, but usually handles IDs for projects.
    
    if (versions.length > 0) {
        const latest = versions[0];
        setVersions(versions); // Store for selector

        if (
          latest.id !== current.versionId &&
          latest.version_number !== current.version
        ) {
          setUpdateAvailable(latest);
        } else {
          setUpdateAvailable(null);
        }
    }
  };

  const openVersionSelector = async () => {
    if (!activeServer?.installed_modpack) return;
    const current = activeServer.installed_modpack;

    // Placeholder pack data from installed info
    const placeholderPack: Modpack = {
      id: current.id,
      name: current.name,
      source: current.source as any,
      summary: linkedDetails?.summary || "Wird geladen...",
      downloads: linkedDetails?.downloads || 0,
      iconUrl: linkedDetails?.iconUrl || "",
      slug: current.id,
    };

    if (!selectedPack || selectedPack.id !== current.id) {
      setSelectedPack(placeholderPack);
    }

    setShowVersionModal(true);

    if (versions.length > 0 && selectedPack?.id === current.id) {
      setLoadingVersions(false);
    } else {
      setLoadingVersions(true);
      setVersions([]);
    }

    // Fetch Full Details & Versions in Parallel
    (async () => {
       // Fetch Full Details
       if (!placeholderPack.iconUrl) {
           const fullPack = await serverStore.fetchModpackDetails(current.id, current.source as any);
           if (fullPack) {
               setLinkedDetails(fullPack);
               setSelectedPack(fullPack);
           }
       }

       // Load Versions
       const fullPack = linkedDetails; // or fetched above
       const slug = fullPack?.slug || current.id;
       const data = await serverStore.fetchModpackVersions(current.id, slug, current.source as any);
       setVersions(data);
       setLoadingVersions(false);
    })();
  };

  const refreshMetadata = async () => {
    if (!activeServer?.installed_modpack) return;
    const current = activeServer.installed_modpack;
    const toastId = toast.loading("Aktualisiere Metadaten...");

    const versions = await serverStore.fetchModpackVersions(current.id, current.id, current.source as any);
      
    // Find current version
    const ver = versions.find((v: any) => v.id === current.versionId || v.version_number === current.version);
      
    if (!ver) {
          toast.error("Version nicht gefunden.", { id: toastId });
          return;
    }

    // Update metadata
    const body = {
    modpackId: current.id,
    modpackName: current.name,
    versionName: current.version,
    versionId: current.versionId,
    source: current.source,
    gameVersions: ver.game_versions,
    loaders: ver.loaders
    };

    const res = await serverStore.installModpack(activeServer.id, "link", body);
    
    if (res.success) {
    toast.success("Metadaten aktualisiert!", { id: toastId });
    serverStore.fetchData();
    } else {
        toast.error(res.message || "Fehler beim Aktualisieren.", { id: toastId });
    }
  };

  const handleManualFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) return;
      const file = e.target.files[0];
      
      if (!file.name.endsWith('.zip')) {
          toast.error("Bitte eine .zip Datei hochladen.");
          return;
      }

      setManualFile(file);
      setManualRestart(true);
      setManualBackup(true);
      setShowManualConfirm(true);
      
      // Reset input value to allow selecting same file again if cancelled
      e.target.value = ''; 
  };

  const confirmManualUpload = async () => {
      if (!manualFile || !activeServer) return;

      setShowManualConfirm(false); // Close modal
      setInstallStatus("installing");
      setInstallMessage("Modpack wird hochgeladen...");
      setUploadPct(0);

      const formData = new FormData();
      formData.append('file', manualFile);
      formData.append('restart', String(manualRestart));
      formData.append('createBackup', String(manualBackup));

      // XHR statt fetch: nur so gibt es echten Upload-Fortschritt (upload.onprogress).
      // Grosse Modpacks sind mehrere hundert MB - der Nutzer soll sehen wie weit er ist.
      // apiUrl() -> respektiert BASE_PATH (funktioniert auch unter /GameManager).
      const token = getToken();
      try {
          await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', apiUrl(`/api/servers/${activeServer.id}/modpack/update/manual`));
              if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

              xhr.upload.onprogress = (e) => {
                  if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
              };
              // Upload fertig -> ab jetzt uebernimmt die serverseitige Install-Progress
              // (activeInstalls via Socket) die Anzeige.
              xhr.upload.onload = () => {
                  setUploadPct(100);
                  setInstallMessage("Upload fertig - Modpack wird installiert...");
              };
              xhr.onload = () => {
                  setUploadPct(null);
                  let data: any = {};
                  try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }
                  if (xhr.status >= 200 && xhr.status < 300 && data.success) {
                      setInstallStatus("complete");
                      toast.success("Modpack erfolgreich hochgeladen und installiert!");
                      setTimeout(() => serverStore.fetchData(), 2000);
                      resolve();
                  } else {
                      setInstallStatus("error");
                      const msg = data.message || `Upload fehlgeschlagen (HTTP ${xhr.status}).`;
                      setInstallMessage(msg);
                      toast.error(msg);
                      reject(new Error(msg));
                  }
              };
              xhr.onerror = () => {
                  setUploadPct(null);
                  setInstallStatus("error");
                  setInstallMessage("Netzwerkfehler beim Upload.");
                  toast.error("Upload fehlgeschlagen.");
                  reject(new Error("network"));
              };
              xhr.send(formData);
          });
      } catch { /* Fehler ist bereits im UI + Toast gemeldet */ } finally {
          setManualFile(null);
      }
  };

  // Installiertes Pack: Details (Icon/Beschreibung) fuer das Hero-Banner nachladen.
  useEffect(() => {
    const cur = activeServer?.installed_modpack;
    if (cur && (!linkedDetails || String(linkedDetails.id) !== String(cur.id))) {
      serverStore.fetchModpackDetails(cur.id, cur.source as any).then((d) => { if (d) setLinkedDetails(d); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServer?.installed_modpack?.id]);

  // Debounced Auto-Suche (Enter/Button funktionieren weiterhin).
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(() => searchModpacks(), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const openPack = (pack: Modpack) => {
    loadVersions(pack);
    setShowVersionModal(true);
  };

  const packUrl = (p: Modpack | null) => !p ? '#' : p.source === 'modrinth'
    ? `https://modrinth.com/modpack/${p.slug}`
    : `https://www.curseforge.com/minecraft/modpacks/${p.slug}`;

  const fmtDownloads = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n || 0);

  const shownPacks = query ? results : trending;
  const installedPack = activeServer?.installed_modpack;

  return (
    <div className="space-y-5 animate-in fade-in duration-300">
      {/* Kopf: Titel + Quelle-Chips + Upload */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="mr-auto">
          <h2 className="text-xl md:text-2xl font-bold text-white flex items-center">
            <Box className="mr-3 text-ember" size={24} /> Modpacks
          </h2>
          <p className="text-fg-muted mt-0.5 text-sm">Installieren, updaten oder eigenes Pack hochladen.</p>
        </div>
        <div className="flex items-center gap-2">
          {(["modrinth", "curseforge"] as const).map((s) => (
            <button key={s} onClick={() => { setSource(s); setResults([]); }}
              className={cn("px-4 py-2 rounded-full text-sm font-semibold border transition-all",
                source === s ? "bg-ember text-white border-ember shadow-[0_0_12px_rgba(255,106,61,0.35)]"
                  : "bg-surface-2 text-fg-muted border-line hover:text-white hover:border-line-strong")}>
              {s === "modrinth" ? "Modrinth" : "CurseForge"}
            </button>
          ))}
          <label className="px-4 py-2 rounded-full text-sm font-semibold border bg-surface-2 text-fg-muted border-line hover:text-white hover:border-line-strong cursor-pointer flex items-center transition-all">
            <Upload size={14} className="mr-2" /> Eigenes Pack (.zip)
            <input type="file" accept=".zip" className="hidden" onChange={handleManualFileSelect} />
          </label>
        </div>
      </div>

      {/* Upload-Fortschritt (Browser -> Server) */}
      {uploadPct !== null && !installState && (
        <Card className="bg-gradient-to-r from-ember/15 to-transparent border-ember/30 relative overflow-hidden">
          <div className="relative z-10 p-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-ember/20 rounded-lg"><Upload className="animate-pulse text-ember" size={20} /></div>
                <div>
                  <h3 className="text-lg font-bold text-white">{uploadPct >= 100 ? "Upload fertig - Installation startet..." : "Modpack wird hochgeladen..."}</h3>
                  <p className="text-xs text-fg-muted font-mono mt-0.5">{manualFile?.name || "Modpack.zip"}</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-white tabular-nums opacity-80">{uploadPct}%</div>
            </div>
            <div className="w-full bg-black/40 rounded-full h-3 overflow-hidden border border-line">
              <div className="bg-gradient-to-r from-ember to-orange-400 h-full transition-all duration-200" style={{ width: `${uploadPct}%` }} />
            </div>
          </div>
        </Card>
      )}

      {/* Sicherheits-Backup VOR einem Update: laeuft minutenlang, bevor die
          eigentliche Installation (installState) startet - ohne dieses Banner
          saehe der Nutzer in der Phase NICHTS. */}
      {backupStore.backupStatus !== "idle" && backupStore.backupServerId === activeServer?.id && !installState && (
        <Card className="bg-gradient-to-r from-blue-500/10 to-transparent border-blue-500/30 relative overflow-hidden">
          <div className="relative z-10 p-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500/20 rounded-lg"><Loader2 className="animate-spin text-blue-400" size={20} /></div>
                <div>
                  <h3 className="text-lg font-bold text-white">Sicherheits-Backup läuft...</h3>
                  <p className="text-xs text-fg-muted mt-0.5">Danach startet automatisch die Installation.</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-white tabular-nums opacity-80">{Math.round(backupStore.backupProgress)}%</div>
            </div>
            <div className="w-full bg-black/40 rounded-full h-3 overflow-hidden border border-line">
              <div className="bg-gradient-to-r from-blue-500 to-blue-400 h-full transition-all duration-300" style={{ width: `${backupStore.backupProgress}%` }} />
            </div>
          </div>
        </Card>
      )}

      {/* Update angestossen, aber noch keine Server-Events (z.B. Warten aufs Backend) */}
      {installStatus === "installing" && !installState && uploadPct === null
        && !(backupStore.backupStatus !== "idle" && backupStore.backupServerId === activeServer?.id) && (
        <div className="flex items-center gap-3 bg-surface-2 border border-line rounded-2xl px-4 py-3">
          <Loader2 className="animate-spin text-ember" size={18} />
          <span className="text-sm text-fg-muted">{installMessage || "Wird gestartet..."}</span>
        </div>
      )}

      {/* Server-seitiger Installations-Fortschritt */}
      {installState && (
        <Card className="bg-gradient-to-r from-ember/15 to-transparent border-ember/30 relative overflow-hidden">
          <div className="relative z-10 p-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-ember/20 rounded-lg"><Loader2 className="animate-spin text-ember" size={20} /></div>
                <div>
                  <h3 className="text-lg font-bold text-white">{installState.status === "complete" ? "Installation fertig" : "Installation läuft..."}</h3>
                  <p className="text-xs text-fg-muted font-mono mt-0.5">{installState.message}</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-white tabular-nums opacity-80">{installState.progress}%</div>
            </div>
            <div className="w-full bg-black/40 rounded-full h-3 overflow-hidden border border-line">
              <div className="bg-gradient-to-r from-ember to-orange-400 h-full transition-all duration-300" style={{ width: `${installState.progress}%` }} />
            </div>
          </div>
        </Card>
      )}

      {/* Installiertes Pack - Hero */}
      {installedPack && (
        <div className="rounded-2xl border border-ember/25 bg-gradient-to-r from-ember/10 via-surface-2 to-surface-2 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-16 h-16 rounded-xl overflow-hidden border border-line-strong bg-ink shrink-0">
            {linkedDetails?.iconUrl
              ? <img src={linkedDetails.iconUrl} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><Box className="text-ember" size={26} /></div>}
          </div>
          <div className="min-w-0 mr-auto">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="info" className="text-[10px] py-0 px-1.5 h-5">INSTALLIERT</Badge>
              <span className="text-[10px] text-fg-muted uppercase font-bold tracking-wider">{installedPack.source}</span>
            </div>
            <h3 className="text-xl font-bold text-white truncate">{installedPack.name}</h3>
            <div className="text-xs text-ember font-mono mt-0.5">v{installedPack.version}</div>
          </div>
          {updateAvailable ? (
            <div className="flex items-center gap-3 bg-green-500/5 px-4 py-2.5 rounded-xl border border-green-500/15">
              <div className="text-right hidden sm:block">
                <div className="text-green-400 font-bold text-sm">Update verfügbar</div>
                <div className="text-green-600 text-xs font-mono">v{updateAvailable.version_number}</div>
              </div>
              <Button onClick={openVersionSelector} variant="success" size="sm"><RefreshCw size={16} className="mr-2" /> Update</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {(!installedPack.gameVersions || installedPack.gameVersions.length === 0) && (
                <Button onClick={refreshMetadata} variant="ghost" size="sm" title="Metadaten aktualisieren" className="text-fg-muted hover:text-white"><RefreshCw size={16} /></Button>
              )}
              <span className="text-fg-muted text-xs flex items-center bg-white/5 px-2.5 py-1.5 rounded-lg border border-line">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2" /> Aktuell
              </span>
              <Button onClick={openVersionSelector} variant="secondary" size="sm">Versionen</Button>
            </div>
          )}
        </div>
      )}

      {/* Kein Pack erkannt -> Verknuepfen */}
      {!installedPack && (
        <div className={cn("border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 transition-all",
          linkingMode ? "bg-amber-500/10 border-amber-500/30" : "bg-surface-2 border-line hover:border-line-strong")}>
          <div className={cn("p-2 rounded-lg shrink-0", linkingMode ? "bg-amber-500/20 text-amber-400" : "bg-white/5 text-fg-muted")}>
            <AlertTriangle size={20} />
          </div>
          <div className="mr-auto">
            <h3 className="font-bold text-fg text-sm">Kein Modpack erkannt</h3>
            <p className="text-xs text-fg-muted mt-0.5">
              {linkingMode
                ? "Verknüpfen-Modus aktiv: wähle unten dein Pack und die Version, die bereits läuft."
                : "Läuft hier schon ein Modpack? Verknüpfe es, um Updates zu bekommen."}
            </p>
          </div>
          <Button onClick={() => setLinkingMode(!linkingMode)} variant={linkingMode ? "warning" : "secondary"} size="sm">
            {linkingMode ? "Abbrechen" : "Verknüpfen"}
          </Button>
        </div>
      )}

      {/* CurseForge-Hinweis */}
      {source === "curseforge" && (
        <div className="flex items-start gap-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            Nicht jedes CurseForge-Pack hat einen Server-Download. Schlägt die Installation fehl:
            offizielles <strong>Server-Pack</strong> aus CurseForge laden und oben über <strong>„Eigenes Pack (.zip)"</strong> hochladen.
          </span>
        </div>
      )}

      {/* Suche */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-muted" size={18} />
        <Input
          placeholder={`Suche auf ${source === "modrinth" ? "Modrinth" : "CurseForge"}... (z.B. ATM, Craftoria, Cobblemon)`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && searchModpacks()}
          className="pl-12 h-12 text-base"
        />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-ember" size={18} />}
      </div>

      {/* Karten-Raster */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-fg-muted flex items-center">
            {query ? `Suchergebnisse (${results.length})` : (<><span className="w-1.5 h-1.5 bg-ember rounded-full mr-2 animate-pulse" /> Beliebt auf {source === "modrinth" ? "Modrinth" : "CurseForge"}</>)}
          </span>
        </div>
        {shownPacks.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-fg-muted opacity-60 border border-dashed border-line rounded-2xl">
            <Layers size={36} className="mb-3" />
            <span className="text-sm">{query ? "Keine Ergebnisse" : "Keine Packs geladen"}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {shownPacks.map((pack) => (
              <button key={pack.id} onClick={() => openPack(pack)}
                className="text-left bg-surface-2 border border-line rounded-2xl p-4 hover:border-ember/40 hover:bg-white/[0.03] transition-all group">
                <div className="flex gap-3">
                  <div className="relative shrink-0">
                    <div className="w-14 h-14 rounded-xl overflow-hidden border border-line bg-ink">
                      {pack.iconUrl
                        ? <img src={pack.iconUrl} alt={pack.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-fg-muted"><Box size={22} /></div>}
                    </div>
                    <span className={cn("absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-surface-2",
                      pack.source === "modrinth" ? "bg-green-500" : "bg-orange-500")} title={pack.source} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-[15px] text-white truncate group-hover:text-ember transition-colors">{pack.name}</h3>
                    <div className="flex items-center gap-1 text-[11px] text-fg-muted mt-0.5">
                      <Download size={11} /> {fmtDownloads(pack.downloads)} Downloads
                    </div>
                  </div>
                </div>
                <p className="text-[12.5px] text-fg-muted line-clamp-2 mt-3 leading-snug min-h-[34px]">{pack.summary || "Keine Beschreibung."}</p>
                <div className="flex items-center justify-end mt-2 text-[11px] text-ember opacity-0 group-hover:opacity-100 transition-opacity">
                  Versionen ansehen <ArrowRight size={12} className="ml-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pack-Detail + Versionen (Modal) */}
      {showVersionModal && selectedPack && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-in fade-in duration-200 p-4"
          onClick={() => setShowVersionModal(false)}>
          <div className="bg-surface border border-line-strong rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            {/* Kopf */}
            <div className="p-5 border-b border-line bg-gradient-to-b from-white/5 to-transparent flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden border border-line-strong bg-ink shrink-0">
                {selectedPack.iconUrl
                  ? <img src={selectedPack.iconUrl} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-fg-muted"><Box size={24} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="neutral" className="uppercase text-[10px] py-0">{selectedPack.source}</Badge>
                  <span className="text-fg-muted text-xs flex items-center"><Download size={12} className="mr-1" /> {fmtDownloads(selectedPack.downloads)}</span>
                </div>
                <h2 className="text-2xl font-bold text-white truncate">{selectedPack.name}</h2>
                <p className="text-xs text-fg-muted line-clamp-1 mt-1">{selectedPack.summary}</p>
                <a href={packUrl(selectedPack)} target="_blank" rel="noreferrer"
                  className="text-ember hover:underline text-xs flex items-center mt-1.5 w-fit">
                  <ExternalLink size={12} className="mr-1" /> Im Browser öffnen
                </a>
              </div>
              <button onClick={() => setShowVersionModal(false)} className="text-fg-muted hover:text-white text-2xl leading-none px-2">&times;</button>
            </div>

            {linkingMode && (
              <div className="px-5 py-2 bg-amber-500/10 border-b border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
                <AlertTriangle size={13} /> Verknüpfen-Modus: es wird NICHTS installiert, nur die Metadaten gesetzt.
              </div>
            )}

            {/* Versionen */}
            <div className="px-5 py-2.5 border-b border-line text-[11px] font-bold uppercase tracking-wider text-fg-muted flex items-center">
              Verfügbare Versionen {loadingVersions && <Loader2 size={13} className="ml-2 animate-spin text-ember" />}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar bg-black/20">
              {loadingVersions ? (
                <div className="flex justify-center py-14"><Loader2 className="animate-spin text-ember" size={30} /></div>
              ) : versions.length === 0 ? (
                <div className="text-center py-14 text-fg-muted text-sm">Keine Versionen gefunden.</div>
              ) : (
                versions.map((ver) => {
                  const isCurrent = activeServer?.installed_modpack?.versionId === ver.id;
                  return (
                    <div key={ver.id} className={cn("flex items-center justify-between gap-3 p-3 rounded-xl border transition-all",
                      isCurrent ? "bg-green-500/10 border-green-500/30" : "bg-surface-2 border-line hover:border-ember/30")}>
                      <div className="min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <span className="font-bold text-fg text-sm">{ver.name || `v${ver.version_number}`}</span>
                          {isCurrent && <Badge variant="success" className="text-[10px] py-0">AKTUELL</Badge>}
                          {ver.featured && <span className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/20">Empfohlen</span>}
                        </div>
                        <div className="flex items-center flex-wrap gap-2 text-[11px] text-fg-muted mt-1">
                          <span>{new Date(ver.date_published).toLocaleDateString()}</span>
                          {(ver.game_versions || []).slice(0, 3).map((gv: string) => (
                            <span key={gv} className="bg-white/5 border border-line px-1.5 py-0.5 rounded">{gv}</span>
                          ))}
                          {(ver.loaders || []).slice(0, 2).map((l: string) => (
                            <span key={l} className="bg-ember/10 text-ember border border-ember/20 px-1.5 py-0.5 rounded capitalize">{l}</span>
                          ))}
                        </div>
                      </div>
                      <Button
                        onClick={() => { setShowVersionModal(false); initiateInstall(ver); }}
                        disabled={isCurrent || installStatus === "installing"}
                        size="sm"
                        variant={isCurrent ? "secondary" : linkingMode ? "warning" : "primary"}
                        className="shrink-0">
                        {isCurrent ? "Installiert" : (<><Download size={14} className="mr-1.5" /> {linkingMode ? "Verknüpfen" : "Installieren"}</>)}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Installation bestaetigen */}
      {installModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-in fade-in p-4">
          <Card className="max-w-md w-full relative overflow-hidden !p-0">
            <div className="p-5 border-b border-line bg-white/5">
              <h3 className="text-xl font-bold text-white">{installModal.action === "install" ? "Installation bestätigen" : "Verknüpfung bestätigen"}</h3>
            </div>
            <div className="p-5 text-fg space-y-4">
              <p className="text-sm">
                <strong>{selectedPack?.name}</strong> Version <strong>v{installModal.version.version_number}</strong> {installModal.action === "install" ? "installieren?" : "verknüpfen?"}
              </p>
              {installModal.action === "install" && (
                <label className="flex items-center cursor-pointer bg-black/30 p-3 rounded-xl border border-line">
                  <input type="checkbox" checked={createBackup} onChange={(e) => setCreateBackup(e.target.checked)} className="w-4 h-4 accent-[#ff6a3d]" />
                  <span className="ml-3 text-sm font-medium">Sicherheits-Backup erstellen</span>
                </label>
              )}
              {installModal.action === "install" && javaVersions.length > 0 && (
                <Select label="Java Version" value={selectedJava} onChange={setSelectedJava}
                  options={javaVersions.map((v: string) => ({ value: v, label: v }))} />
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setInstallModal(null)}>Abbrechen</Button>
                <Button variant="primary" onClick={confirmInstall}>{installModal.action === "install" ? "Installieren" : "Verknüpfen"}</Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Eigenes Pack: Upload bestaetigen */}
      {showManualConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-in fade-in p-4">
          <Card className="max-w-md w-full relative overflow-hidden !p-0">
            <div className="p-5 border-b border-line bg-white/5">
              <h3 className="text-xl font-bold text-white">Eigenes Pack installieren</h3>
            </div>
            <div className="p-5 text-fg space-y-4">
              <div className="flex items-center p-3 bg-ember/10 border border-ember/20 rounded-xl">
                <Box size={22} className="text-ember mr-3 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-fg">Datei ausgewählt</div>
                  <div className="text-xs text-ember break-all">{manualFile?.name}</div>
                </div>
              </div>
              <p className="text-sm text-fg-muted">
                Überschreibt Mods und Configs — <strong>Welten und Spielerdaten bleiben erhalten</strong>.
              </p>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer bg-black/30 p-3 rounded-xl border border-line">
                  <input type="checkbox" checked={manualBackup} onChange={(e) => setManualBackup(e.target.checked)} className="w-4 h-4 accent-[#ff6a3d]" />
                  <span className="ml-3 text-sm font-medium">Sicherheits-Backup erstellen</span>
                </label>
                <label className="flex items-center cursor-pointer bg-black/30 p-3 rounded-xl border border-line">
                  <input type="checkbox" checked={manualRestart} onChange={(e) => setManualRestart(e.target.checked)} className="w-4 h-4 accent-[#ff6a3d]" />
                  <span className="ml-3 text-sm font-medium">Server nach Update neustarten</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => { setShowManualConfirm(false); setManualFile(null); }}>Abbrechen</Button>
                <Button variant="primary" onClick={confirmManualUpload}><Upload size={16} className="mr-2" /> Hochladen & installieren</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
});
